/**
 * Admin/agent triggers — request access to a vendor's dashboard (and, once
 * approved, jump into "view as" mode). One component owns both the trigger
 * pill and the dialog so callers just drop `<VendorAccessTrigger vendor={v} />`
 * into a table row.
 */
import { useEffect, useState } from "react";
import api, { formatApiError, setViewAsVendor } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Eye, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

function _fmt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export default function VendorAccessTrigger({ vendor }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [grants, setGrants] = useState([]);

  // Pull all of "my" grants so we know whether this vendor is already approved
  // (→ render a "View as" pill) or has a pending request.
  useEffect(() => {
    let cancelled = false;
    api.get("/vendor-access/mine")
      .then(({ data }) => { if (!cancelled) setGrants(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]); // refresh after every dialog interaction

  const activeGrant = grants.find((g) => g.vendor_id === vendor.id && g.is_active);
  const pendingGrant = grants.find((g) => g.vendor_id === vendor.id && g.status === "pending");

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/vendor-access/request", { vendor_id: vendor.id, reason: reason.trim() || null });
      toast.success("Request sent to vendor");
      setOpen(false);
      setReason("");
      const { data } = await api.get("/vendor-access/mine");
      setGrants(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Already-active grant → "View as" pill (no dialog needed).
  if (activeGrant) {
    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setViewAsVendor(vendor.id, vendor.business_name || vendor.name, "/dashboard/vendor")}
        className="rounded-full text-xs h-7 text-emerald-700 hover:bg-emerald-50"
        title={`Active until ${_fmt(activeGrant.expires_at)}`}
        data-testid={`view-as-${vendor.id}`}
      >
        <Eye className="h-3.5 w-3.5 mr-1" />View as · until {_fmt(activeGrant.expires_at)}
      </Button>
    );
  }

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setOpen(true)}
        className="rounded-full text-xs h-7"
        disabled={!!pendingGrant}
        title={pendingGrant ? "Request pending vendor approval" : "Request dashboard access"}
        data-testid={`request-access-${vendor.id}`}
      >
        <KeyRound className="h-3.5 w-3.5 mr-1" />
        {pendingGrant ? "Pending…" : "Request access"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request access to {vendor.business_name || vendor.name}</DialogTitle>
            <DialogDescription>
              The vendor will see this request in their inbox and choose whether to approve it (default 7 day expiry).
              Once approved, you can read every page they see and add new vehicles on their behalf.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-neutral-500">Reason (optional, shown to vendor)</label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Quarterly audit of fleet listings…"
              className="rounded-xl"
              data-testid="request-access-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="request-access-submit">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5 mr-1.5" />}
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
